import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Col,
  Row,
  ModalBody,
  Label,
  Input,
  Modal,
  Form,
  Button,
  FormFeedback,
  Spinner,
} from "reactstrap";

import * as Url from "../../../api/url_helper";
import { APIClient } from "../../../api/api_helper";
import LogoDark from "../../../assets/images/logo-dark.svg";
import {
  GetUserData,
  showToastMessage,
} from "../../service/CommonService";

/* ---------- API ---------- */

const api = new APIClient();

/* ---------- Types ---------- */

interface UserSessionVerifyProps {
  isOpen: boolean;
  toggle: () => void;
  click: () => void;
}

interface FormValues {
  password: string;
}

/* ---------- Component ---------- */

const UserSessionVerify: React.FC<UserSessionVerifyProps> = ({
  isOpen,
  toggle,
  click,
}) => {
  const [passwordShow, setPasswordShow] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  /* ---------- Formik ---------- */

  const formik = useFormik<FormValues>({
    initialValues: {
      password: "",
    },
    validationSchema: Yup.object({
      password: Yup.string().required("Password is required."),
    }),
    onSubmit: async (values) => {
      const user = GetUserData();

      const payload = {
        userid: user?.userid,
        userpassword: values.password,
        issso: 0,
      };

      setLoader(true);

      api
        .create(Url.Admin_API.AUTH_API + "UserSessionVerify", payload)
        .then((response: any) => {
          setLoader(false);

          if (response?.data?.isvalid) {
            toggle();
            click();
          } else {
            formik.resetForm();
            showToastMessage("error", response?.message || "Invalid password");
          }
        })
        .catch((error: unknown) => {
          setLoader(false);
          showToastMessage("error", String(error));
        });
    },
  });

  /* ---------- Render ---------- */

  return (
    <Modal
      id="showModal"
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="md"
      backdrop="static"
      zIndex={1100}
      onClosed={() => formik.resetForm()}
    >
      <ModalBody>
        <Row>
          <Col lg={12}>
            <div className="text-center text-white-50">
              <div className="d-inline-block auth-logo">
                <img src={LogoDark} alt="Logo" style={{ height: "40px" }} />
              </div>
            </div>
          </Col>
        </Row>

        <div className="align-items-center d-flex pt-4">
          <h3 className="ms-2 mb-0 flex-grow-1 text-dark">
            Sign in{" "}
            <i className="ri-lock-2-line text-muted fw-light fs-6 ps-2" />
          </h3>
        </div>

        <div className="p-2 mt-4">
          <Form onSubmit={formik.handleSubmit}>
            <div className="mb-3">
              <Label htmlFor="password-input" className="form-label">
                Password <span className="text-danger">*</span>
              </Label>

              <div className="position-relative auth-pass-inputgroup mb-3">
                <Input
                  id="password-input"
                  name="password"
                  type={passwordShow ? "text" : "password"}
                  className="form-control pe-5"
                  placeholder="Enter Password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!formik.touched.password &&
                    !!formik.errors.password
                  }
                />

                {formik.touched.password &&
                  formik.errors.password && (
                    <FormFeedback>
                      {formik.errors.password}
                    </FormFeedback>
                  )}

                <button
                  type="button"
                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                  onClick={() => setPasswordShow((prev) => !prev)}
                >
                  {passwordShow ? (
                    <i className="mdi mdi-eye-outline align-bottom fs-18" />
                  ) : (
                    <i className="mdi mdi-eye-off-outline align-bottom fs-18" />
                  )}
                </button>
              </div>
            </div>

            <div className="mt-4 text-center">
              <Button
                color="primary"
                type="submit"
                className="btn w-100 btn-load"
                disabled={loader}
              >
                {loader && (
                  <Spinner size="sm" className="me-2">
                    Loading...
                  </Spinner>
                )}
                Sign In
              </Button>
            </div>
          </Form>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default UserSessionVerify;
